'use client';

import { useCallback, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Check, Copy, Loader2, ShieldAlert, ShieldCheck } from 'lucide-react';
import { Link } from '@/i18n/routing';
import { Instrument, Polya, useStrana, zaprosit } from './obshchee';

/**
 * «Мой IP»: оба адреса посетителя сразу, провайдер и признак хостинга,
 * плюс проверка любого IP или домена.
 *
 * Через Cloudflare браузер ходит к нам одним протоколом — тем, что
 * предпочитает сам (обычно IPv6, если он есть). Второй адрес узнаём у
 * ipify: `api.ipify.org` отвечает только по IPv4, `api6.ipify.org` —
 * только по IPv6. Запрос идёт из браузера напрямую, наш сервер этот
 * адрес видит только когда мы спрашиваем про него провайдера, и не
 * сохраняет.
 */

export interface InfoIp {
  ip: string;
  versiya: 4 | 6;
  ptr?: string;
  provajder?: { asn: number; imya: string; set?: string; strana?: string };
  set?: { nazvanie?: string; organizaciya?: string; strana?: string; set?: string };
  geo: { strana?: string; region?: string; gorod?: string };
  hosting: boolean;
  tor?: boolean;
  domen?: string;
  adresa?: string[];
}

async function ipify(url: string): Promise<string | null> {
  try {
    const r = await fetch(url, { cache: 'no-store', signal: AbortSignal.timeout(4000) });
    const j = (await r.json()) as { ip?: string };
    return typeof j.ip === 'string' ? j.ip : null;
  } catch {
    return null;
  }
}

function Kopirovat({ tekst }: { tekst: string }) {
  const t = useTranslations('dh_set_ui');
  const [gotovo, setGotovo] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard.writeText(tekst).then(() => {
          setGotovo(true);
          setTimeout(() => {
            setGotovo(false);
          }, 1500);
        });
      }}
      aria-label={t('kopirovat')}
      className="rounded-md p-1.5 text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text)]"
    >
      {gotovo ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
    </button>
  );
}

/** Карточка сведений об адресе — и для своего, и для проверенного. */
export function KartochkaIp({ d }: { d: InfoIp }) {
  const t = useTranslations('dh_set_ui');
  const strana = useStrana();
  const mesto = [d.geo.gorod, d.geo.region, strana(d.geo.strana)].filter(Boolean).join(', ');
  return (
    <div className="space-y-4">
      {d.domen ? (
        <p className="text-sm text-[var(--color-text-muted)]">
          {t('domen_adresa', { domen: d.domen, adresa: (d.adresa ?? []).join(', ') })}
        </p>
      ) : null}
      <Polya
        stroki={[
          [
            t('pole_ip'),
            <span key="ip" className="font-mono">
              {d.ip}
            </span>,
          ],
          [
            t('pole_provajder'),
            d.provajder ? `${d.provajder.imya} (AS${String(d.provajder.asn)})` : t('neizvestno'),
          ],
          [t('pole_mesto'), mesto],
          [
            t('pole_strana_seti'),
            d.provajder?.strana || d.set?.strana
              ? strana(d.provajder?.strana ?? d.set?.strana)
              : '',
          ],
          [
            t('pole_set'),
            [d.set?.nazvanie, d.provajder?.set ?? d.set?.set].filter(Boolean).join(' · '),
          ],
          [t('pole_organizaciya'), d.set?.organizaciya],
          [
            t('pole_ptr'),
            d.ptr ? (
              <span key="ptr" className="font-mono">
                {d.ptr}
              </span>
            ) : (
              ''
            ),
          ],
          [
            t('pole_tip'),
            d.tor ? (
              <span
                key="tip"
                className="inline-flex items-center gap-1.5 text-[var(--color-warning-400)]"
              >
                <ShieldAlert className="h-4 w-4" aria-hidden />
                {t('tip_tor')}
              </span>
            ) : d.hosting ? (
              <span
                key="tip"
                className="inline-flex items-center gap-1.5 text-[var(--color-warning-400)]"
              >
                <ShieldAlert className="h-4 w-4" aria-hidden />
                {t('tip_hosting')}
              </span>
            ) : (
              <span
                key="tip"
                className="inline-flex items-center gap-1.5 text-[var(--color-success-400)]"
              >
                <ShieldCheck className="h-4 w-4" aria-hidden />
                {t('tip_obychnyj')}
              </span>
            ),
          ],
        ]}
      />
      <p className="text-xs leading-relaxed text-[var(--color-text-muted)]">{t('pro_tip')}</p>
    </div>
  );
}

function StrokaAdresa({
  metka,
  ip,
  zhdyom,
}: {
  metka: string;
  ip: string | null;
  zhdyom: boolean;
}) {
  const t = useTranslations('dh_set_ui');
  return (
    <div className="flex items-center gap-3 border-b border-[var(--color-border)] px-5 py-4 last:border-b-0">
      <span className="w-12 shrink-0 text-sm font-medium text-[var(--color-text-muted)]">
        {metka}
      </span>
      {zhdyom ? (
        <Loader2 className="h-5 w-5 animate-spin text-[var(--color-text-muted)]" aria-hidden />
      ) : ip ? (
        <>
          <span className="min-w-0 flex-1 font-mono text-xl font-semibold [overflow-wrap:anywhere] sm:text-2xl">
            {ip}
          </span>
          <Kopirovat tekst={ip} />
        </>
      ) : (
        <span className="text-[var(--color-text-muted)]">{t('ne_obnaruzhen')}</span>
      )}
    </div>
  );
}

export function MojIp() {
  const t = useTranslations('dh_set_ui');
  const [svoj, setSvoj] = useState<InfoIp | null>(null);
  const [v4, setV4] = useState<string | null>(null);
  const [v6, setV6] = useState<string | null>(null);
  const [zhdyom, setZhdyom] = useState(true);
  const [oshibka, setOshibka] = useState(false);

  const uznat = useCallback(async () => {
    setZhdyom(true);
    const [nash, a4, a6] = await Promise.all([
      zaprosit<InfoIp>('ip', ''),
      ipify('https://api.ipify.org?format=json'),
      ipify('https://api6.ipify.org?format=json'),
    ]);
    if (nash.vid === 'gotovo') {
      setSvoj(nash.dannye);
      // Своему серверу верим больше: ipify мог не ответить.
      setV4(nash.dannye.versiya === 4 ? nash.dannye.ip : a4);
      setV6(nash.dannye.versiya === 6 ? nash.dannye.ip : a6 && a6.includes(':') ? a6 : null);
    } else {
      setOshibka(true);
      setV4(a4);
      setV6(a6 && a6.includes(':') ? a6 : null);
    }
    setZhdyom(false);
  }, []);

  useEffect(() => {
    void uznat();
  }, [uznat]);

  return (
    <div className="space-y-10">
      <section>
        <div className="overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-card)]">
          <StrokaAdresa metka="IPv4" ip={v4} zhdyom={zhdyom} />
          <StrokaAdresa metka="IPv6" ip={v6} zhdyom={zhdyom} />
        </div>
        <div className="mt-6">
          {svoj ? <KartochkaIp d={svoj} /> : null}
          {oshibka && !svoj ? (
            <p className="text-sm text-[var(--color-text-muted)]">{t('oshibka_upstream')}</p>
          ) : null}
        </div>
        <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <Link
            href="/app/proxy-checker"
            className="font-medium hover:text-[var(--color-accent-400)]"
          >
            {t('ssylka_proxy_check')} →
          </Link>
          <Link href="/app/proxy-list" className="font-medium hover:text-[var(--color-accent-400)]">
            {t('ssylka_proxy_list')} →
          </Link>
        </div>
      </section>

      <section>
        <h2 className="mb-4 text-2xl font-semibold">{t('proverit_ip_zagolovok')}</h2>
        <Instrument<InfoIp>
          instrument="ip"
          placeholder={t('placeholder_ip')}
          primery={['8.8.8.8', '1.1.1.1', 'wikipedia.org', '2606:4700:4700::1111']}
          pokazat={(d) => <KartochkaIp d={d} />}
        />
      </section>
    </div>
  );
}
